import { Component, Inject } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface TagActionDialogData {
  mode: 'rename' | 'delete';
  name: string;
  // Products carrying the tag, shown so the user sees what a change reaches.
  count: number;
}

/**
 * Confirms a change that reaches every product carrying a tag. Closes with
 * the new name (rename), true (delete), or undefined when cancelled.
 */
@Component({
  selector: 'app-tag-action-dialog',
  template: `
    <h2 mat-dialog-title>{{ (data.mode === 'rename' ? 'tags.renameTitle' : 'tags.deleteTitle') | translate }}</h2>
    <mat-dialog-content>
      <p class="tag-action__message">
        {{ (data.mode === 'rename' ? 'tags.renameMessage' : 'tags.deleteMessage') | translate: { name: data.name, count: data.count } }}
      </p>
      <mat-form-field *ngIf="data.mode === 'rename'" appearance="outline" floatLabel="always" class="w-100">
        <mat-label>{{ 'tags.newName' | translate }}</mat-label>
        <input matInput [formControl]="name" maxlength="50" autocomplete="off" (keydown.enter)="confirm()">
        <mat-error *ngIf="name.hasError('comma')">{{ 'tags.noComma' | translate }}</mat-error>
      </mat-form-field>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>{{ 'cancel' | translate }}</button>
      <button mat-flat-button
              [color]="data.mode === 'delete' ? 'warn' : 'primary'"
              [disabled]="data.mode === 'rename' && !canRename"
              (click)="confirm()">
        {{ (data.mode === 'rename' ? 'save' : 'delete') | translate }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .tag-action__message { margin: 0 0 16px; color: var(--tp-ink-2); line-height: 1.5; }
  `]
})
export class TagActionDialogComponent {
  name = new FormControl('', [Validators.required, Validators.maxLength(50)]);

  constructor(
    private _dialogRef: MatDialogRef<TagActionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TagActionDialogData
  ) {
    this.name.setValue(data.name);
    this.name.valueChanges.subscribe(value => {
      if ((value || '').includes(',')) {
        this.name.setErrors({ comma: true });
      }
    });
  }

  get canRename(): boolean {
    const value = (this.name.value || '').trim();
    return this.name.valid && value !== '' && value !== this.data.name;
  }

  confirm(): void {
    if (this.data.mode === 'delete') {
      this._dialogRef.close(true);
    } else if (this.canRename) {
      this._dialogRef.close(this.name.value.trim());
    }
  }
}
